import React from "react";
import { motion , Variants, easeOut} from "framer-motion";
import { CheckCircle, ShoppingCart, TrendingUp, ShieldCheck, Palette, Rocket, Cpu } from "lucide-react";

// Import your images from the assets folder
import shopifyHeroBg from "../../../assets/IT-services/shopifybg.webp";
import shopifyImg from "../../../assets/IT-services/shopify.png";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

// Animation variants (keeping the existing fadeUp)
const fadeUp: Variants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: easeOut,
    },
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const ShopifyServices = () => {
  return (
    <div className="bg-white text-gray-800 overflow-x-hidden"> {/* Added overflow-x-hidden to the root div to prevent horizontal scroll issues */}
      {/* 🚀 Hero Section - Clean & Impactful */}

      <Helmet>
        <title>Shopify Development Services | Nadezhda Venture</title>
        <meta
          name="description"
          content="Nadezhda Venture offers custom Shopify development services including theme customization, SEO, and ongoing support. Best IT Training in Noida."
        />
        <meta
          name="keywords"
          content="Shopify Development, Best IT Training in Noida, Shopify SEO, Theme Customization, Nadezhda Venture"
        />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <section
        className="relative h-[70vh] sm:h-[60vh] md:h-[70vh] w-full flex items-center justify-center text-white bg-cover bg-center overflow-hidden" // Adjusted sm:h for slightly larger phones
        style={{ backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url(${shopifyHeroBg})` }} // Ensured gradient direction
      >
        {/* Transparent Image - positioned absolutely to cover the hero section subtly */}
        <motion.img
          className="absolute inset-0 w-full h-full object-cover z-0" // Correct positioning
          initial={{ scale: 1.2, opacity: 0 }}
          animate={{ scale: 1, opacity: 0.2 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
        />

        {/* Text content - placed on top of all backgrounds/overlays */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 text-center py-16 md:py-0">
          <motion.h1
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-4 drop-shadow-lg" // More granular font sizing
            variants={fadeUp}
            initial="hidden"
            animate="visible"
          >
            Launch Your Success on <span className="text-orange-600">Shopify</span>
          </motion.h1>
          <motion.p
            className="text-base sm:text-lg md:text-xl max-w-3xl mx-auto mb-8 opacity-90 px-2"
            variants={fadeUp}
            initial="hidden"
            animate="visible"
          >
            We build high-performing, visually stunning, and conversion-focused Shopify stores that empower your brand to thrive in the digital marketplace.
          </motion.p>
          <motion.a
           
            // className="inline-block bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-8 rounded-full text-base sm:text-lg shadow-lg transition-all duration-300 transform hover:scale-105"
            variants={fadeUp}
            initial="hidden"
            animate="visible"
          >
            <Link
  to="/contact"
  className="inline-block bg-blue-700 hover:bg-blue-800 text-white px-8 py-4 rounded-2xl font-semibold shadow-lg transition-colors duration-300 text-lg"
>
  Get a Free Consultation
</Link>
           
          </motion.a>
        </div>
      </section>

      {/* --- */}

      {/* ✨ Introduction & Core Value - White Background */}
      <section className="py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
              Your Vision, Our Shopify Expertise.
            </h2>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-3 md:mb-4">
              Navigating the eCommerce landscape can be complex. That's why we offer end-to-end Shopify development services, from initial concept to ongoing support, ensuring a seamless and successful online presence.
            </p>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
              We focus on delivering **responsive, secure, and user-friendly stores** designed to convert visitors into loyal customers. Let us handle the technical complexities while you focus on your products and growth.
            </p>
          </motion.div>
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}>
            <img
              src={shopifyImg}
              alt="Illustration showcasing Shopify design service"
              className="rounded-xl shadow-2xl w-full h-auto mt-8 md:mt-0"
            />
          </motion.div>
        </div>
      </section>

      {/* --- */}

      {/* 🌟 What We Offer - Feature Grid */}
      <section className="py-12 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <motion.h2
            className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-8 md:mb-12"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            Comprehensive Shopify Services
          </motion.h2>
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            {[
              { icon: <ShoppingCart size={32} className="text-orange-500 mb-3" />, title: "Custom Store Development", desc: "Unique designs built to reflect your brand identity and vision." },
              { icon: <Palette size={32} className="text-blue-500 mb-3" />, title: "Theme Customization", desc: "Tailoring existing themes to fit your specific functional and aesthetic needs." },
              { icon: <ShieldCheck size={32} className="text-green-500 mb-3" />, title: "App & Integration", desc: "Seamless integration of essential apps for enhanced functionality." },
              { icon: <TrendingUp size={32} className="text-purple-500 mb-3" />, title: "Performance Optimization", desc: "Ensuring fast load times and smooth user experience for conversions." },
              { icon: <Rocket size={32} className="text-red-500 mb-3" />, title: "SEO for Shopify", desc: "Optimizing your store to rank higher and attract more organic traffic." },
              { icon: <CheckCircle size={32} className="text-teal-500 mb-3" />, title: "Ongoing Support & Maintenance", desc: "Reliable post-launch support to keep your store running flawlessly." },
            ].map((service, i) => (
              <motion.div
                key={i}
                className="bg-white p-6 sm:p-8 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
              >
                <div className="flex justify-center mb-3">{service.icon}</div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">{service.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{service.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* --- */}

      {/* 📈 Our Transparent Process - Step-by-step with icons */}
      <section className="py-12 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <motion.h2
            className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-8 md:mb-12"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            Our Streamlined Development Process
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {[
              {
                icon: <CheckCircle size={36} className="text-blue-600 mb-3" />,
                title: "1. Discovery & Strategy",
                desc: "We understand your brand, goals, and target audience to lay a solid foundation for your store.",
              },
              {
                icon: <Palette size={36} className="text-orange-600 mb-3" />,
                title: "2. Design & UX",
                desc: "Crafting intuitive user experiences and visually appealing designs that captivate your customers.",
              },
              {
                icon: <Cpu size={36} className="text-green-600 mb-3" />,
                title: "3. Development & Build",
                desc: "Bringing your design to life with clean code, seamless functionality, and robust integrations.",
              },
              {
                icon: <Rocket size={36} className="text-red-600 mb-3" />,
                title: "4. Launch & Optimize",
                desc: "Thorough testing before launch, followed by ongoing optimization and support for continuous growth.",
              },
            ].map((step, i) => (
              <motion.div
                key={i}
                className="bg-gray-50 p-6 sm:p-8 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
              >
                <div className="flex justify-center mb-3">{step.icon}</div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-sm text-gray-600">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* --- */}

      {/* 💬 Call to Action - Simple & Direct */}
      {/* <section className="bg-gradient-to-r from-blue-800 to-orange-600 text-white py-12 md:py-20 text-center">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <motion.h2
            className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 md:mb-6"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            Ready to Build Your Dream Shopify Store?
          </motion.h2>
          <motion.p
            className="text-base sm:text-lg opacity-90 mb-6 md:mb-8 px-2"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            Let's discuss your eCommerce goals and bring your vision to life.
          </motion.p>
          <motion.a
            href="#contact"
            className="inline-block bg-white text-orange-600 font-semibold py-3 px-8 rounded-full text-base sm:text-lg shadow-lg transition-all duration-300 transform hover:scale-105 hover:text-orange-700"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            Get Started Today
          </motion.a>
        </div>
      </section> */}
    </div>
  );
};

export default ShopifyServices;